import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { SeoHead } from "@/components/resources/SeoHead";
import { Mail } from "lucide-react";

const META = {
  title: "Press & Media — NetLift",
  description: "Media resources, company facts, and announcements from NetLift, the AI Value Management platform. Press contact and boilerplate included.",
  robots: "index,follow",
  canonical: "https://netlift.app/press",
  og_title: "NetLift Press & Media",
  og_description: "Announcements, company facts and media resources from NetLift.",
  og_url: "https://netlift.app/press",
};

const FACTS = [
  ["Founded", "2026"],
  ["Founder", "Paige Gilmore"],
  ["Category", "AI Value Management (AVM)"],
  ["Product", "SaaS platform measuring the cost, time savings and ROI of AI adoption"],
  ["Data", "295+ published resources; AI vendor pricing re-verified daily against official pages"],
  ["Pricing", "From $199/month · 14-day free trial"],
];

export default function Press() {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead meta={META} />
      <Navbar />
      <main className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
        <span className="text-xs font-semibold uppercase tracking-wide text-editorial">Press &amp; Media</span>
        <h1 className="mt-2 font-heading text-4xl font-semibold leading-tight text-navy sm:text-5xl">
          NetLift newsroom
        </h1>
        <p className="mt-4 max-w-xl text-base text-slate-600">
          Resources for journalists, analysts, and partners covering AI adoption, AI ROI, and software spend.
        </p>

        <section className="mt-12" data-testid="press-announcement">
          <h2 className="font-heading text-2xl font-semibold text-navy">Latest announcement</h2>
          <article className="mt-4 rounded-2xl border border-slate-200 bg-white p-8">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Announcement · 2026</p>
            <h3 className="mt-2 font-heading text-xl font-semibold text-navy">
              NetLift launches AI Value Management platform with daily-verified AI pricing index
            </h3>
            <p className="mt-3 leading-relaxed text-slate-600">
              NetLift today announced the launch of its AI Value Management platform, giving businesses a
              standard way to measure what AI tools cost, the time they save, and the return they deliver.
              Alongside the platform, NetLift publishes one of the web&rsquo;s largest continuously-verified
              indexes of AI tool pricing — over 100 vendor sources checked daily against official pricing
              pages — plus free ROI calculators and evaluation guides used by teams comparing AI spend.
            </p>
            <p className="mt-3 leading-relaxed text-slate-600">
              &ldquo;Every business is buying AI, and almost none can prove it&rsquo;s working,&rdquo; said Paige Gilmore,
              founder of NetLift. &ldquo;We built the measurement layer: tracked work on one side, verified
              vendor pricing on the other, and a CFO-ready value report in the middle.&rdquo;
            </p>
          </article>
        </section>

        <section className="mt-10" data-testid="press-facts">
          <h2 className="font-heading text-2xl font-semibold text-navy">Company facts</h2>
          <dl className="mt-4 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
            {FACTS.map(([k, v]) => (
              <div key={k} className="flex gap-6 px-6 py-3.5 text-sm">
                <dt className="w-28 shrink-0 font-medium text-slate-400">{k}</dt>
                <dd className="text-slate-700">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="mt-10" data-testid="press-boilerplate">
          <h2 className="font-heading text-2xl font-semibold text-navy">Boilerplate</h2>
          <p className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 text-sm leading-relaxed text-slate-600">
            <strong>About NetLift</strong> — NetLift is the AI Value Management platform. It helps
            businesses measure the cost, time savings, and return of AI adoption with tracked work,
            daily-verified vendor pricing, and finance-ready reporting. Save Time. Show Return. Learn
            more at netlift.app.
          </p>
        </section>

        <section className="mt-10" data-testid="press-contact">
          <h2 className="font-heading text-2xl font-semibold text-navy">Media contact</h2>
          <a href="mailto:paige@netlift.app?subject=Media%20enquiry"
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-editorial hover:text-editorial"
            data-testid="press-contact-email">
            <Mail className="h-4 w-4" /> paige@netlift.app
          </a>
          <p className="mt-2 text-xs text-slate-400">Interviews, data requests (AI pricing trends), and commentary on AI ROI.</p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
